/**
 * "Your Privacy Choices" — the persistent CCPA/CPRA advertising opt-out (and
 * opt-in) control, mounted in the site footer.
 *
 * Opt-out regions (US/other) grant advertising consent by default — see
 * `src/lib/consent/regions.ts`. This control is the required, always-available way
 * for any visitor to change that:
 *   • "Opt out of advertising" → writes 'ads_optout' (analytics kept, ads OFF).
 *   • "Allow advertising"      → writes 'granted'    (analytics + ads ON).
 * Both push a Consent Mode v2 update and notify same-tab pixels immediately so the
 * Google Ads / Meta / TikTok tags mount or unmount without a page reload.
 */
'use client';

import { useEffect, useState } from 'react';
import {
  applyConsent,
  notifyConsentChange,
  readStoredConsent,
  writeStoredConsent,
} from '@/lib/consent/apply';

export default function PrivacyChoices() {
  const [open, setOpen] = useState(false);
  const [adsOn, setAdsOn] = useState<boolean | null>(null);

  // Read the current ad-consent state straight from the cookie, then open. Done
  // in the click handler (not an effect) so the dialog always reflects live state.
  const openDialog = () => {
    const s = readStoredConsent();
    setAdsOn(s === 'granted' || s === 'analytics_ads');
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const optOut = () => {
    writeStoredConsent('ads_optout');
    applyConsent({ analytics: true, ads: false });
    notifyConsentChange();
    setAdsOn(false);
  };
  const optIn = () => {
    writeStoredConsent('granted');
    applyConsent({ analytics: true, ads: true });
    notifyConsentChange();
    setAdsOn(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="v4-footer-link"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          minHeight: 44,
          padding: 0,
          fontSize: 14,
          color: 'var(--paper)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textDecoration: 'none',
          fontFamily: 'inherit',
        }}
      >
        Your Privacy Choices
      </button>

      {open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(2px)',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Your Privacy Choices"
            style={{
              width: '100%',
              maxWidth: 440,
              background: 'var(--dark-card, #11151c)',
              border: '1px solid var(--hair, rgba(255,255,255,0.12))',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
              color: 'var(--paper, #fff)',
            }}
          >
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>Your Privacy Choices</h2>
            <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.5, color: 'var(--dim, #9aa4b2)' }}>
              We use advertising cookies to show you relevant BizRnR ads and measure
              campaigns. You can opt out of the sale/sharing of your information for
              advertising at any time. Analytics that keep the site working are
              unaffected. See our{' '}
              <a href="/privacy" style={{ color: 'var(--paper, #fff)', textDecoration: 'underline' }}>
                privacy policy
              </a>
              .
            </p>

            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--dim, #9aa4b2)' }}>
              Advertising cookies are currently{' '}
              <strong style={{ color: 'var(--paper, #fff)' }}>
                {adsOn === null ? '…' : adsOn ? 'ON' : 'OFF'}
              </strong>
              .
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
              {adsOn ? (
                <button
                  type="button"
                  onClick={optOut}
                  style={{
                    minHeight: 44,
                    padding: '10px 16px',
                    borderRadius: 10,
                    border: '1px solid var(--hair, rgba(255,255,255,0.2))',
                    background: 'transparent',
                    color: 'var(--paper, #fff)',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Opt out of advertising
                </button>
              ) : (
                <button
                  type="button"
                  onClick={optIn}
                  style={{
                    minHeight: 44,
                    padding: '10px 16px',
                    borderRadius: 10,
                    border: 'none',
                    background: 'var(--primary-700, #2563eb)',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Allow advertising
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{
                  minHeight: 44,
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: '1px solid var(--hair, rgba(255,255,255,0.2))',
                  background: 'transparent',
                  color: 'var(--dim, #9aa4b2)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
